/**
 * Durable session storage in IndexedDB through the raw API (no idb dependency). The pop-out
 * restores the launch response from here after a push wake or a reload so it can REGISTER
 * without a new launch token. Every operation has a timeout; a broken IndexedDB (private
 * window, quota) degrades to memory so the phone still runs for the tab's lifetime.
 */
import { z } from 'zod';
import { LaunchResponse } from '@ccphone/contracts';
import { withTimeout } from '../keepalive/timers.js';

export const StoredSession = z.object({
  launch: LaunchResponse,
  /** ISO, local clock, when the record was written */
  stored_at: z.string(),
  browser_id: z.string(),
  /** server minus local clock at the last sync, ms */
  clock_offset_ms: z.number().default(0),
});
export type StoredSession = z.infer<typeof StoredSession>;

export interface SessionStore {
  get(): Promise<StoredSession | null>;
  put(s: StoredSession): Promise<void>;
  clear(): Promise<void>;
}

export class MemorySessionStore implements SessionStore {
  private value: StoredSession | null = null;
  async get(): Promise<StoredSession | null> {
    return this.value;
  }
  async put(s: StoredSession): Promise<void> {
    this.value = StoredSession.parse(s);
  }
  async clear(): Promise<void> {
    this.value = null;
  }
}

const DB_NAME = 'ccphone';
const STORE = 'session';
const KEY = 'current';

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('indexeddb request failed'));
  });
}

export class IndexedDbSessionStore implements SessionStore {
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(
    private readonly factory: IDBFactory,
    private readonly timeoutMs = 5000,
  ) {}

  static available(): boolean {
    try {
      return typeof indexedDB !== 'undefined' && indexedDB !== null;
    } catch {
      return false;
    }
  }

  private open(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = withTimeout(
        new Promise<IDBDatabase>((resolve, reject) => {
          const r = this.factory.open(DB_NAME, 1);
          r.onupgradeneeded = () => {
            const db = r.result;
            if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
          };
          r.onsuccess = () => resolve(r.result);
          r.onerror = () => reject(r.error ?? new Error('indexeddb open failed'));
          r.onblocked = () => reject(new Error('indexeddb open blocked'));
        }),
        this.timeoutMs,
        'indexeddb open',
      ).catch((e: unknown) => {
        this.dbPromise = null;
        throw e;
      });
    }
    return this.dbPromise;
  }

  private async tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await this.open();
    return withTimeout(
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        t.onabort = () => reject(t.error ?? new Error('indexeddb transaction aborted'));
        req(fn(t.objectStore(STORE))).then(resolve, reject);
      }),
      this.timeoutMs,
      `indexeddb ${mode}`,
    );
  }

  async get(): Promise<StoredSession | null> {
    const raw = await this.tx<unknown>('readonly', (s) => s.get(KEY));
    if (raw === undefined || raw === null) return null;
    const parsed = StoredSession.safeParse(raw);
    return parsed.success ? parsed.data : null;
  }

  async put(s: StoredSession): Promise<void> {
    await this.tx('readwrite', (st) => st.put(StoredSession.parse(s), KEY));
  }

  async clear(): Promise<void> {
    await this.tx('readwrite', (st) => st.delete(KEY));
  }
}

/** IndexedDB when it works, memory otherwise (never throws). */
export function openSessionStore(): SessionStore {
  return IndexedDbSessionStore.available() ? new IndexedDbSessionStore(indexedDB) : new MemorySessionStore();
}
